import { useEffect } from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://bluebirdhaus.com';

const lodgingSchema = {
  '@context': 'https://schema.org',
  '@type': 'LodgingBusiness',
  name: 'Bluebirdhaus Luxury Vacation Rentals',
  description:
    'Luxury Austin bachelor party and bachelorette party house rentals. Resort-style pools, hot tubs, game rooms, and sports courts for groups of 12–22.',
  url: `${SITE_URL}/`,
  image:
    'https://a0.muscache.com/im/pictures/miso/Hosting-889843382811703189/original/d9f61d52-56a6-4e25-b17e-53960e86395e.jpeg',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Austin',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 30.2672,
    longitude: -97.7431,
  },
  amenityFeature: [
    { '@type': 'LocationFeatureSpecification', name: 'Resort-Style Pool', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'Luxury Hot Tub', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'Game Room', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'Pickleball Court', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'Fire Pit', value: true },
    { '@type': 'LocationFeatureSpecification', name: 'Karaoke Stage', value: true },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.94',
    reviewCount: '200',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'How many guests can stay at your Austin party houses?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Stoneridge Estate sleeps up to 22 guests across 6 bedrooms and 12 beds — perfect for large bachelor or bachelorette party groups. The Woodshire comfortably sleeps 12 guests across 4 bedrooms and 8 beds, ideal for smaller, more intimate celebrations. Both properties are exclusively yours for the entire stay.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is the pool heated at your Austin rental properties?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Both Stoneridge Estate and The Woodshire feature heated pools so your group can swim and celebrate any time of year. Whether you\'re planning a summer bachelorette bash or a winter bachelor weekend, the pool is always ready. Stoneridge also includes a hot tub for the ultimate relaxation experience.',
      },
    },
    {
      '@type': 'Question',
      name: 'Are your properties suitable for bachelor and bachelorette parties?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolutely — our properties are specifically designed with bachelor and bachelorette parties in mind. Both estates feature open-concept entertainment spaces, outdoor pools, full kitchens for catering, karaoke setups, and plenty of room for the whole crew to hang out. We welcome celebration groups and want your party weekend to be unforgettable.',
      },
    },
    {
      '@type': 'Question',
      name: 'How far are the properties from Austin\'s nightlife and bars?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both estates are just 20–22 minutes from Austin\'s legendary 6th Street Entertainment District, Rainey Street bar scene, and the Red River Cultural District. You\'re close enough to enjoy the best nightlife in Texas, yet tucked away in a private, peaceful setting for when the party comes back home.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the minimum stay for a bachelor or bachelorette party booking?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We typically require a 2-night minimum stay for weekend bookings, which is perfect for a full bachelor or bachelorette party weekend. For peak dates like holidays and major Austin events (SXSW, ACL Fest), a 3-night minimum may apply. Contact us directly for custom arrangements or mid-week group stays.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is there a noise curfew or party policy?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'We ask that outdoor music and noise be kept to a reasonable level after 10 PM out of respect for neighbors. Indoor celebrations can continue throughout the night. Both properties are set on spacious lots with privacy, so your group has plenty of freedom to celebrate. We just ask that all guests treat the property with care.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can we bring our own catering or food and drinks?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes! Both properties have fully equipped, large kitchens perfect for self-catering. You\'re welcome to bring your own food, drinks, and hire outside catering services. We can also connect you with local Austin catering companies and bartending services that specialize in bachelor and bachelorette party events.',
      },
    },
    {
      '@type': 'Question',
      name: 'What amenities are included for party groups?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Both estates come fully loaded: heated pool, outdoor entertainment areas, full kitchen, smart TVs, high-speed WiFi, and dedicated parking. Stoneridge Estate additionally features a hot tub, karaoke system, and a large covered outdoor patio that seats the whole group. Everything you need for an epic Austin party weekend is already there.',
      },
    },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Austin Bachelor & Bachelorette Party Rentals | Bluebirdhaus Luxury Villas',
  description:
    'The ultimate Austin bachelor party and bachelorette party venue. Bluebirdhaus luxury vacation rentals feature resort pools, hot tubs, game rooms & sports courts — perfect for groups of 12–22.',
  url: `${SITE_URL}/`,
  inLanguage: 'en-US',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Bluebirdhaus',
    url: `${SITE_URL}/`,
  },
};

function injectSchema(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    (el as HTMLScriptElement).type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SeoSchemas() {
  useEffect(() => {
    injectSchema('schema-lodging', lodgingSchema);
    injectSchema('schema-faq', faqSchema);
    injectSchema('schema-webpage', webPageSchema);
  }, []);

  return null;
}
